<script setup lang="ts">
  import ChevronDown from "@/components/icons/ChevronDown.vue";
  import ChevronUp from "@/components/icons/ChevronUp.vue";

  type Props = {
    label: string;
    // Number of selected items inside the body; hidden when 0.
    count?: number;
  };

  const props = withDefaults(defineProps<Props>(), { count: 0 });

  // Controlled open state via v-model:open. A parent that tracks the currently
  // open id can implement single-open semantics.
  const open = defineModel<boolean>("open", { default: false });

  function toggle() {
    open.value = !open.value;
  }
</script>

<template>
  <section :class="$style.base">
    <button
      type="button"
      :class="$style.header"
      :aria-expanded="open"
      :aria-label="props.label"
      @click="toggle"
    >
      <span :class="$style.label">{{ props.label }}</span>
      <span :class="$style.meta">
        <span v-if="props.count > 0" :class="$style.count">{{ props.count }}</span>
        <ChevronUp v-if="open" :size="18" />
        <ChevronDown v-else :size="18" />
      </span>
    </button>
    <div v-if="open" :class="$style.content">
      <slot />
    </div>
  </section>
</template>

<style lang="scss" module>
  .base {
    display: flex;
    flex-direction: column;

    // border-bottom: 1px solid var(--ksd-divider-color);
  }

  .header {
    width: 100%;
    padding: 12px 8px;

    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;

    background: transparent;
    border: none;
    color: var(--foreground);
    cursor: pointer;

    font-family: inherit;
    font-size: 0.875rem;
    line-height: 1.25rem;
    text-align: left;
  }

  .label {
    font-weight: 500;
  }

  .meta {
    display: flex;
    align-items: center;
    gap: 8px;

    color: var(--muted-foreground);
  }

  .count {
    min-width: 1.25rem;
    padding: 0 0.375rem;

    display: inline-flex;
    align-items: center;
    justify-content: center;

    background-color: var(--accent, var(--muted, transparent));
    color: var(--brand);
    border-radius: 999px;

    font-size: 0.75rem;
    line-height: 1rem;
    font-weight: 600;
  }

  .content {
    padding: 8px 0 12px;
  }
</style>
